import { BRAND, INK, STATUS } from 'Utils/styleTokens';
import { FILE_TYPE_LABELS } from 'DiscoveryReports/ReportPanel/OverviewPanel/OverviewPanel';
import { engineLabels } from 'Utils';
import { uiLabel } from 'Utils/Labeling';
import { isFileLikeDatasourceType } from 'Utils/fileTypeUtils';

// Tile / pill styling per severity. Graded levels reuse the status-dot hues (the same ramp
// SEVERITY_RAMP uses); CLEAN is the product's "secured" green; NEVER_SCANNED is neutral + dashed.
export const SEVERITY_STYLE = {
  CRITICAL: { label: 'Critical', bg: STATUS.danger.dot, fg: '#FFFFFF', pillBg: STATUS.danger.bg, pillFg: STATUS.danger.text },
  HIGH: { label: 'High', bg: STATUS.orange.dot, fg: '#FFFFFF', pillBg: STATUS.orange.bg, pillFg: STATUS.orange.text },
  MEDIUM: { label: 'Medium', bg: STATUS.warning.dot, fg: '#FFFFFF', pillBg: STATUS.warning.bg, pillFg: STATUS.warning.text },
  LOW: { label: 'Low', bg: BRAND.blue, fg: '#FFFFFF', pillBg: STATUS.info.bg, pillFg: STATUS.info.text },
  CLEAN: { label: 'Clean', bg: STATUS.success.bg, fg: STATUS.success.text, pillBg: STATUS.success.bg, pillFg: STATUS.success.text },
  NEVER_SCANNED: { label: 'Never scanned', bg: INK[100], fg: INK[500], pillBg: INK[100], pillFg: INK[500], dashed: true },
};

export const DATA_CLASS_LABELS = {
  STRUCTURED: 'Structured',
  SEMI_STRUCTURED: 'Semi-structured',
  UNSTRUCTURED: 'Unstructured',
};

export const SEVERITY_RANK = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, CLEAN: 4, NEVER_SCANNED: 5 };

// Entity category chips on the detail page (Credentials / Payment / PII). Static sets: the
// credential family is exactly the worker's rules-only secret labels, payment is the PCI /
// banking family; everything else is PII.
const CREDENTIAL_ENTITIES = new Set([
  'PASSWORD', 'CONNECTION_STRING', 'VENDOR_API_KEY', 'PAYMENT_API_KEY', 'CLOUD_ACCESS_KEY',
  'PRIVATE_KEY', 'AUTH_SECRET', 'JWT_TOKEN', 'SEED_PHRASE',
]);
const PAYMENT_ENTITIES = new Set([
  'CREDIT_CARD', 'CVV', 'CARD_TRACK_DATA', 'CARD_EXPIRY', 'IBAN', 'BANK_ACCOUNT', 'ROUTING_NUMBER',
  'ACH_TRACE_NUMBER', 'MICR', 'SWIFT_BIC', 'CRYPTO_WALLET', 'PIN',
]);

export const ENTITY_CATEGORIES = [
  { key: 'cred', label: 'Credentials' },
  { key: 'payment', label: 'Payment' },
  { key: 'pii', label: 'PII' },
];

export function entityCategory(code) {
  const c = String(code || '').toUpperCase();
  if (CREDENTIAL_ENTITIES.has(c)) return 'cred';
  if (PAYMENT_ENTITIES.has(c)) return 'payment';
  return 'pii';
}

export function sourceTypeLabel(source) {
  if (!source) return '';
  // Tiles are small — the long "Delimited (CSV, TSV, pipe, etc.)" label is shortened here only.
  if (source.datasourceType === 'FILE_DELIMITED_FOLDER') return 'Delimited';
  const byType = FILE_TYPE_LABELS[source.datasourceType];
  if (byType) return byType;
  if (source.datasourceType === 'NOSQL') return `NoSQL · ${engineLabels[source.engine] || source.engine || ''}`.trim();
  return engineLabels[source.engine] || source.engine || 'RDBMS';
}

/**
 * Tile sizing for the home grid: sources ordered by severity then finding volume; the largest
 * source with findings spans 3×2, the second 2×2, everything else 1×1. Sources without a
 * completed run or without findings are always 1×1 and sort last.
 *
 * A source flagged `managed` (the `state` tag — someone is handling it by hand, see ../stateTag)
 * sinks BELOW every automated source and is never promoted to a feature tile, whatever its
 * severity: it will never be masked or cleaned by C2, so its risk does not belong front and
 * centre. It is still on the grid, striped, because it is still part of the estate.
 */
export function sizeTiles(sources) {
  const list = (Array.isArray(sources) ? sources : []).slice().sort((a, b) => {
    const m = (a.managed ? 1 : 0) - (b.managed ? 1 : 0);
    if (m !== 0) return m;
    const r = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9);
    if (r !== 0) return r;
    if (tileWeight(b) !== tileWeight(a)) return tileWeight(b) - tileWeight(a);
    return String(a.name).localeCompare(String(b.name));
  });
  const withFindings = list.filter((s) => !s.managed && tileWeight(s) > 0 && s.severity !== 'NEVER_SCANNED');
  const biggest = withFindings.slice().sort((a, b) => tileWeight(b) - tileWeight(a));
  const first = biggest[0]?.id;
  const second = biggest[1]?.id;
  return list.map((source) => ({
    source,
    span: source.id === first ? '3x2' : source.id === second ? '2x2' : '1x1',
  }));
}

/**
 * Open FIELDS for the queue bar and the tile sizing: the columns / files that still carry a
 * finding (the diff's persisting + new). A source with one completed run has a baseline diff, so
 * this is its whole field count; a source the worker could not diff has none and weighs nothing.
 *
 * It used to fall back to `findingTotal`, which is entity INSTANCES — a different unit, and one
 * that is orders of magnitude larger, so a single un-diffable source dwarfed the whole queue.
 */
export function queueCount(source) {
  return source?.openFindings != null ? source.openFindings : 0;
}

/** What a tile is sized and ranked by: the same open-field count the queue bars use. */
export function tileWeight(source) {
  return queueCount(source);
}

/**
 * How a run is named wherever it is picked or compared: "#48 · Sep 9 2026 2:31 PM".
 *
 * Runs carry the full year and the time of day, unlike the ledgers' compact date columns. Two
 * runs of the same source on the same day are ordinary — a re-run after a fix, a nightly plus a
 * manual scan — and "#48 · Sep 9" against "#47 · Sep 9" gives the user nothing to tell them
 * apart in the compare pickers.
 */
export function runLabel(run) {
  if (!run) return '—';
  const date = run.completedAt ? new Date(run.completedAt) : null;
  const when = date && !Number.isNaN(date.getTime())
    ? `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} `
      + `${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    : '';
  return when ? `#${run.jobId} · ${when}` : `#${run.jobId}`;
}

/** The mock's per-row note: first seen / gone since / partially cleaned / unchanged. */
export function rowNote(row, fromLabel, toLabel) {
  if (row.status === 'NEW') return `first seen in ${toLabel}`;
  if (row.status === 'RESOLVED') return `gone since ${fromLabel}`;
  if (row.countFrom != null && row.countTo != null && row.countTo < row.countFrom) {
    return `partially cleaned — ${row.countTo} remain`;
  }
  if (row.countFrom != null && row.countTo != null && row.countTo > row.countFrom) return 'growing';
  return 'unchanged';
}

/**
 * The "Change" column: current count, then the movement since the earlier run in brackets —
 * "35 (↑ 35)" for a new row, "4 (↓ 2)" for a partial clean-up, "41 (no change)".
 */
export function changeLabel(row) {
  const cur = row.countTo == null ? 0 : row.countTo;
  const prev = row.countFrom == null ? 0 : row.countFrom;
  const d = cur - prev;
  if (d === 0) return `${cur.toLocaleString()} (no change)`;
  return `${cur.toLocaleString()} (${d > 0 ? '↑' : '↓'} ${Math.abs(d).toLocaleString()})`;
}

/**
 * When a diff row first showed up, as far as the compared pair can tell: a NEW row appeared in
 * the newer run; a PERSISTING or RESOLVED row was already there in the older run, so that run's
 * completion is the earliest date we can vouch for. Returns null when the date is unknown.
 */
export function firstSeenAt(row, fromCompletedAt, toCompletedAt) {
  const raw = row.status === 'NEW' ? toCompletedAt : fromCompletedAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Sep 1 · 14d" — first-seen date plus its age in days (today = 0d); "—" when unknown. */
export function ageLabel(row, fromCompletedAt, toCompletedAt, now = Date.now()) {
  const d = firstSeenAt(row, fromCompletedAt, toCompletedAt);
  if (!d) return '—';
  const days = Math.max(0, Math.floor((now - d.getTime()) / 86400000));
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${date} · ${days}d`;
}

/** Pill colours for the per-entity landscape severity level on the detail rows. */
export const LEVEL_PILL = {
  CRITICAL: { label: 'Critical', bg: SEVERITY_STYLE.CRITICAL.pillBg, fg: SEVERITY_STYLE.CRITICAL.pillFg },
  HIGH: { label: 'High', bg: SEVERITY_STYLE.HIGH.pillBg, fg: SEVERITY_STYLE.HIGH.pillFg },
  MEDIUM: { label: 'Medium', bg: SEVERITY_STYLE.MEDIUM.pillBg, fg: SEVERITY_STYLE.MEDIUM.pillFg },
  LOW: { label: 'Low', bg: SEVERITY_STYLE.LOW.pillBg, fg: SEVERITY_STYLE.LOW.pillFg },
};

/** entity code → CRITICAL | HIGH | MEDIUM | LOW from the advanced-config landscape severity tiers. */
export function levelMapFromTiers(tiers) {
  const map = {};
  (Array.isArray(tiers) ? tiers : []).forEach((t) => {
    (t.entities || []).forEach((code) => { map[String(code).toUpperCase()] = t.level; });
  });
  return map;
}

export function spanLabel(row) {
  const f = row.countFrom == null ? 0 : row.countFrom;
  const t = row.countTo == null ? 0 : row.countTo;
  return `${f} → ${t}`;
}

/** Stable identity of a diff row for selection — the same (kind, location, entity) key the worker diffs on. */
export function diffRowKey(row) {
  return `${row.locationKind}:${row.location}:${row.entity}`;
}

/** Rows a person can act on: RESOLVED findings are already gone, so they are never handed off. */
export function isRemediable(row) {
  return row.status !== 'RESOLVED';
}

/**
 * Flatten selected diff rows into the remediation handoff CSV: one row per (location, entity)
 * with the source, the run pair, the category, the counts and the same note the table shows.
 * Column order is the CSV column order.
 */
export function buildHandoffRows(rows, {
  sourceName = '', fromLabel = '', toLabel = '', levelByEntity = {}, fromCompletedAt = null, toCompletedAt = null,
} = {}) {
  return (Array.isArray(rows) ? rows : []).map((r) => ({
    data_source: sourceName,
    status: r.status,
    location: r.location,
    location_kind: r.locationKind,
    entity: r.entity,
    entity_label: uiLabel(r.entity),
    category: ENTITY_CATEGORIES.find((c) => c.key === entityCategory(r.entity))?.label || 'PII',
    level: LEVEL_PILL[levelByEntity[String(r.entity).toUpperCase()]]?.label || '',
    count_from: r.countFrom == null ? 0 : r.countFrom,
    count_to: r.countTo == null ? 0 : r.countTo,
    first_seen: (() => { const d = firstSeenAt(r, fromCompletedAt, toCompletedAt); return d ? d.toISOString() : ''; })(),
    note: rowNote(r, fromLabel, toLabel),
    compared_from: fromLabel,
    compared_to: toLabel,
    schema: r.schema || '',
    table: r.table || '',
    field: r.field || '',
    file_path: r.filePath || '',
  }));
}

/**
 * Findings are counted in one of two UNITS, chosen per source so that the headline number here
 * matches the one the SAME source reports on its Data Source Risk Profile:
 *
 *   LOCATION  relational / NoSQL — distinct COLUMNS (fields) carrying a finding, which is what
 *             the risk profile's "N sensitive fields" counts. A column that matched three entity
 *             types is one column, even though the ledger lists it as three rows.
 *   INSTANCE  file-based sources — TOTAL findings, i.e. the sum of the per-entity counts, the
 *             same unit as the landscape tile's findingTotal. A document span's location is the
 *             file itself, so there is no column to dedupe on and the honest total is how many
 *             findings were made.
 *
 * Only the counts change: the ledger is still one row per (location, entity), because that is the
 * grain the entity labels, the severity level and the handoff CSV live at.
 */
export const COUNT_UNIT = { LOCATION: 'LOCATION', INSTANCE: 'INSTANCE' };

export function countUnitForSource(source) {
  return isFileLikeDatasourceType(source?.datasourceType) ? COUNT_UNIT.INSTANCE : COUNT_UNIT.LOCATION;
}

/**
 * What one diff row contributes in INSTANCE terms — its current count, or, for a RESOLVED row
 * (countTo null), the count it had in the "from" run, so resolved work is credited at its size.
 */
export function instancesOf(row) {
  if (row?.countTo != null) return Number(row.countTo) || 0;
  return Number(row?.countFrom) || 0;
}

/**
 * The key a row is deduped on under LOCATION. `location` is the worker's own RowKey.location()
 * and is what the ledger shows; the structural fallback covers a payload that omitted it, where
 * keying on '' would silently collapse a whole table into one column.
 */
export function locationKeyOf(row) {
  if (row?.location) return row.location;
  const parts = [row?.schema, row?.table, row?.field].filter(Boolean);
  if (parts.length) return parts.join('.');
  return row?.filePath || '';
}

/** Count diff rows in the given unit. */
export function countFindings(rows, unit) {
  const list = Array.isArray(rows) ? rows : [];
  if (unit === COUNT_UNIT.INSTANCE) return list.reduce((n, r) => n + instancesOf(r), 0);
  return new Set(list.map(locationKeyOf)).size;
}

/**
 * Put every distinct location in `rows` into exactly ONE bucket. Returns a Map of
 * locationKeyOf(row) → 'NEW' | 'PERSISTING' | 'RESOLVED'.
 *
 * A column is NEW only when the "from" run held nothing there at all. If the column already
 * carried a finding — the same entity (a PERSISTING row) or a different one that has since gone
 * (a RESOLVED row) — it is PERSISTING no matter how many further entity types it has picked up.
 * Detecting another entity in a column you were already tracking is not a new exposure; the
 * column was on the list before and is still on it. A location with no open row left is RESOLVED.
 *
 * Row-level status is untouched — the ledger still labels each (location, entity) row on its own,
 * so a PERSISTING column can and does list NEW rows underneath it.
 */
export function bucketLocations(rows) {
  const seen = new Map();   // key → { open, existedBefore }
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const key = locationKeyOf(r);
    const e = seen.get(key) || { open: false, existedBefore: false };
    if (r.status === 'NEW') e.open = true;
    if (r.status === 'PERSISTING') { e.open = true; e.existedBefore = true; }
    if (r.status === 'RESOLVED') e.existedBefore = true;
    seen.set(key, e);
  });
  const out = new Map();
  seen.forEach((e, key) => {
    if (!e.open) out.set(key, 'RESOLVED');
    else out.set(key, e.existedBefore ? 'PERSISTING' : 'NEW');
  });
  return out;
}

/**
 * {resolved, persisting, new} in the source's own unit.
 *
 * Under LOCATION the three buckets PARTITION the distinct columns — they sum to the total —
 * because `bucketLocations` gives each column one status. Under INSTANCE they are per-row sums,
 * which partition the findings for the same reason.
 */
export function diffTotalsIn(rows, unit) {
  const list = Array.isArray(rows) ? rows : [];
  if (unit === COUNT_UNIT.INSTANCE) {
    const of = (status) => countFindings(list.filter((r) => r.status === status), unit);
    return { resolved: of('RESOLVED'), persisting: of('PERSISTING'), new: of('NEW') };
  }
  const totals = { resolved: 0, persisting: 0, new: 0 };
  bucketLocations(list).forEach((status) => {
    if (status === 'RESOLVED') totals.resolved += 1;
    else if (status === 'PERSISTING') totals.persisting += 1;
    else totals.new += 1;
  });
  return totals;
}

/** Noun for whatever the counts above are counting — "columns" / "fields" / "findings". */
export function countNoun(source, { plural = true } = {}) {
  if (countUnitForSource(source) === COUNT_UNIT.INSTANCE) return plural ? 'findings' : 'finding';
  if (source?.datasourceType === 'NOSQL') return plural ? 'fields' : 'field';
  return plural ? 'columns' : 'column';
}
